const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR;

// A class longer than this can no longer be marked as recurring (occurrences would overlap).
export const MAX_RECURRING_DURATION_IN_MINUTES = 8 * MINUTES_IN_HOUR;

// The duration dialog has two halves: a clock (hours + minutes) and a days-only field.
// Exactly one half is active at a time.
export type DurationMode = "time" | "days";

export interface IDurationDialogState {
  mode: DurationMode;
  hours: number;
  minutes: number;
  days: number;
}

export interface IDurationParts {
  days: number;
  hours: number;
  minutes: number;
}

export const splitDuration = (
  totalMinutes: number | null | undefined,
): IDurationParts => {
  const total = Math.max(0, Math.round(Number(totalMinutes) || 0));
  return {
    days: Math.floor(total / MINUTES_IN_DAY),
    hours: Math.floor((total % MINUTES_IN_DAY) / MINUTES_IN_HOUR),
    minutes: total % MINUTES_IN_HOUR,
  };
};

// Pre-fill the dialog when editing: whole-day durations open the days half, everything
// else opens the clock half (with the days field seeded to a sensible value).
export const initDurationDialog = (
  totalMinutes: number | null | undefined,
): IDurationDialogState => {
  const { days, hours, minutes } = splitDuration(totalMinutes);

  if (days > 0 && hours === 0 && minutes === 0) {
    return { mode: "days", days, hours: 0, minutes: 0 };
  }

  return {
    mode: "time",
    hours: hours + days * 24,
    minutes,
    days: days || 1,
  };
};

export const dialogToMinutes = ({
  mode,
  hours,
  minutes,
  days,
}: IDurationDialogState): number => {
  if (mode === "days") {
    return Math.max(0, Number(days) || 0) * MINUTES_IN_DAY;
  }

  return (
    Math.max(0, Number(hours) || 0) * MINUTES_IN_HOUR +
    Math.max(0, Number(minutes) || 0)
  );
};

// Human-readable duration: "8 дни", "8 дни 45 мин", "1 ч 45 мин", "45 мин".
export const formatDuration = (
  totalMinutes: number | null | undefined,
): string => {
  const { days, hours, minutes } = splitDuration(totalMinutes);
  const parts: string[] = [];

  if (days > 0) parts.push(`${days} ${days === 1 ? "ден" : "дни"}`);
  if (hours > 0) parts.push(`${hours} ч`);
  if (minutes > 0) parts.push(`${minutes} мин`);

  return parts.length > 0 ? parts.join(" ") : "0 мин";
};

// A duration of a full day or more is shown as a span ("16:00 · 8 дни") rather than a
// clock range; anything shorter keeps the "16:00 - 19:00" time range. Driven purely by
// the duration so it behaves identically across calendars and the event profile.
export const isMultiDayDuration = (
  totalMinutes: number | null | undefined,
): boolean => (Number(totalMinutes) || 0) >= MINUTES_IN_DAY;

// A service's buffer after the reservation, shown next to its time: "+30 мин". Empty when
// there is no buffer.
export const formatBufferAfter = (
  bufferMinutes: number | null | undefined,
): string => {
  const buffer = Math.max(0, Number(bufferMinutes) || 0);
  return buffer > 0 ? `+${formatDuration(buffer)}` : "";
};

// Calendar label: a "16:00 - 19:00" time range for sub-day events, or the start time plus
// the duration ("16:00 · 8 дни") for events lasting a full day or more. A service's buffer
// after the reservation follows the range: "16:00 - 17:00 +30 мин".
export const getEventCalendarTimeLabel = ({
  startTime,
  endTime,
  durationInMinutes,
  bufferAfterMinutes,
}: {
  startTime?: string;
  endTime?: string;
  durationInMinutes?: number;
  bufferAfterMinutes?: number;
}): string => {
  if (startTime === "00:00" && durationInMinutes === MINUTES_IN_DAY) {
    return "Цял ден";
  }

  const label = isMultiDayDuration(durationInMinutes)
    ? `${startTime ?? ""} · ${formatDuration(durationInMinutes)}`
    : `${startTime ?? ""} - ${endTime ?? ""}`;
  const buffer = formatBufferAfter(bufferAfterMinutes);

  return buffer ? `${label} ${buffer}` : label;
};
